/* eslint-disable @typescript-eslint/no-explicit-any */
import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  CreateOrUpdateRentalListingRequest,
  PmBuildingType,
  RentalListingClient,
  RentalListingDto,
} from '@core/api';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { LocationFormlyFieldService } from '@core/services/formly-service/location-formly-field.service';
import { MtxGridColumn } from '@ng-matero/extensions/grid';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { finalize } from 'rxjs';
import { RentalListingEditComponent } from './components';

interface ExtendedRentalListingDto extends RentalListingDto {
  addressForSorting: string;
  cityForSorting: string;
  fullAddress: string;
  titleForSorting: string;
  descriptionForSorting: string;
  specificPriceForSorting: string;
  areaSpecificationForSorting: string;
  buildingTypeForSorting: string;
  isPublishedForSorting: string;
  linksForSorting: string;
}

@Component({
  selector: 'app-website-edit',
  templateUrl: './website-edit.component.html',
  styleUrls: ['./website-edit.component.scss'],
  standalone: false,
})
export class WebsiteEditComponent implements OnInit, AfterViewInit {
  form = new FormGroup({});
  formFields: Array<FormlyFieldConfig>;
  isLoading = false;
  formModel = {};
  searchform = new FormGroup({
    searchTerm: new FormControl(''),
  });

  public rentalListingObjects: ExtendedRentalListingDto[];
  public createOrUpdateRentalListingRequest: CreateOrUpdateRentalListingRequest;
  public filteredRentalListingObjects: ExtendedRentalListingDto[];
  searchTerm = '';

  columnsRentalListings: MtxGridColumn[] = [];

  headline = this.translateService.instant('website_edit.rental.headline');
  isLoadingObjects = false;

  buildingTypes: PmBuildingType[];

  constructor(
    private translateService: TranslateService,
    private fieldService: LocationFormlyFieldService,
    private breadcrumbService: BreadcrumbService,
    private rentalListingClient: RentalListingClient,
    private dialog: MatDialog
  ) {
    this.breadcrumbService.setBreadcrumbs([{ label: this.headline }]);
  }

  ngOnInit(): void {
    this.isLoadingObjects = true;

    this.columnsRentalListings = [
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__archobjectId'
        ),
        field: 'archObjId',
        sortable: true,
        width: '5%',
        minWidth: 100,
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__address'
        ),
        field: 'addressForSorting',
        sortable: true,
        formatter: (data: any) => data.fullAddress || '',
        width: '15%',
        minWidth: 200,
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__title'
        ),
        field: 'titleForSorting',
        sortable: true,
        formatter: data => WebsiteEditComponent.shortenText(data.title, 50),
        width: '15%',
        minWidth: 200,
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__price'
        ),
        field: 'specificPriceForSorting',
        sortable: true,
        formatter: data => {
          const shortenedPrice = WebsiteEditComponent.shortenText(
            data.specificPrice,
            50
          );
          return shortenedPrice && shortenedPrice.length > 0
            ? shortenedPrice
            : this.translateService.instant(
                'rem.properties.label__price_on_request'
              );
        },
        width: '15%',
        minWidth: 200,
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__buildingType'
        ),
        field: 'buildingTypeForSorting',
        sortable: true,
        formatter: data => {
          return data.buildingTypes
            .sort((a: any, b: any) =>
              a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            )
            .map((bt: any) => bt.name)
            .join(', ');
        },
        width: '15%',
        minWidth: 200,
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__area'
        ),
        field: 'areaSpecificationForSorting',
        sortable: true,
        formatter: data =>
          WebsiteEditComponent.shortenText(data.areaSpecification, 50) ?? '--',
        width: '15%',
        minWidth: 200,
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__description'
        ),
        field: 'descriptionForSorting',
        sortable: true,
        width: '30%',
        minWidth: 300,
        formatter: data =>
          WebsiteEditComponent.shortenText(data.description, 50),
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__link'
        ),
        field: 'linksForSorting',
        sortable: true,
        width: '10%',
        minWidth: 150,
        formatter: data => {
          if (!data.links || data.links.length === 0) {
            return '';
          }

          const formattedLinks = data.links.map(link => {
            const shortenedLink = WebsiteEditComponent.shortenText(
              link.link,
              30
            );

            return link.linkDisplayName
              ? `<small class="text-muted">${link.linkDisplayName}</small><br>(${shortenedLink})`
              : shortenedLink;
          });

          return formattedLinks.join('<br><br>');
        },
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__isPublished'
        ),
        field: 'isPublishedForSorting',
        type: 'boolean',
        sortable: true,
        width: '5%',
        minWidth: 100,
        formatter: data =>
          data.isPublished
            ? this.translateService.instant(
                'website_edit.table_header.label__true'
              )
            : this.translateService.instant(
                'website_edit.table_header.label__false'
              ),
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__createdAt'
        ),
        field: 'createdAt',
        sortable: true,
        width: '10%',
        minWidth: 150,
        formatter: data =>
          data.createdAt
            ? moment
                .utc(data.createdAt)
                .tz('Europe/Berlin')
                .locale(this.translateService.instant('locale.locale'))
                .format(
                  this.translateService.instant(
                    'locale.moment__date_time_format'
                  )
                ) +
              ' ' +
              this.translateService.instant('website_edit.extended_time_str')
            : 'Nicht verfügbar',
      },
      {
        header: this.translateService.instant(
          'website_edit.table_header.label__changedAt'
        ),
        field: 'changeDate',
        sortable: true,
        width: '10%',
        minWidth: 150,
        formatter: data =>
          data.changeDate
            ? moment
                .utc(data.changeDate)
                .tz('Europe/Berlin')
                .locale(this.translateService.instant('locale.locale'))
                .format(
                  this.translateService.instant(
                    'locale.moment__date_time_format'
                  )
                ) +
              ' ' +
              this.translateService.instant('website_edit.extended_time_str')
            : 'Nicht verfügbar',
      },
    ];

    this.getRentalListingObjects();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const headers = document.querySelectorAll('.mtx-grid .mat-sort-header');
      headers.forEach(header => {
        (header as HTMLElement).setAttribute('tabindex', '0');
      });
    });
  }

  /**
   * Applies the search filter to the rental marketing objects.
   */
  applySearch() {
    this.searchTerm = this.searchform.get('searchTerm')?.value ?? '';
    if (this.searchTerm.trim().length > 2) {
      const searchTermLower = this.searchTerm.toLowerCase();

      this.filteredRentalListingObjects = this.rentalListingObjects.filter(
        rentalListingObject =>
          rentalListingObject.address?.city
            ?.toLowerCase()
            .includes(searchTermLower) ||
          rentalListingObject.address?.postalCode
            ?.toLowerCase()
            .includes(searchTermLower) ||
          rentalListingObject.address?.streetName
            ?.toLowerCase()
            .includes(searchTermLower) ||
          rentalListingObject.address?.houseNumber
            ?.toLowerCase()
            .includes(searchTermLower) ||
          rentalListingObject.title?.toLowerCase().includes(searchTermLower) ||
          rentalListingObject.description
            ?.toLowerCase()
            .includes(searchTermLower) ||
          rentalListingObject.archObjId
            ?.toLowerCase()
            .includes(searchTermLower) ||
          rentalListingObject.addressForSorting
            ?.toLowerCase()
            .includes(searchTermLower) ||
          rentalListingObject.fullAddress
            ?.toLowerCase()
            .includes(searchTermLower) ||
          rentalListingObject.links?.some(
            item =>
              item.link?.toLowerCase().includes(searchTermLower) ||
              item.linkDisplayName?.toLowerCase().includes(searchTermLower)
          )
      );
    } else {
      this.filteredRentalListingObjects = [...this.rentalListingObjects];
    }
  }

  /**
   * Handles errors by logging them and setting the loading state to false.
   * @param error The error to handle.
   */
  private handleError(error: Error) {
    this.isLoading = false;
    console.error('[WebsiteEditComponent] handleError:', error);
  }

  /**
   * Retrieves the rental marketing objects and updates the filtered list.
   */
  public getRentalListingObjects() {
    this.rentalListingClient.getAllRentalListingObjectsForEdit().subscribe({
      next: (rentalMarketingObjects: RentalListingDto[]) => {
        const processedObjects = rentalMarketingObjects.map(obj => {
          return {
            ...obj,
            addressForSorting: obj.address
              ? `${obj.address.streetName || ''} ${obj.address.houseNumber || ''}`.trim()
              : '',
            cityForSorting: obj.address?.city || '',
            fullAddress: obj.address
              ? (() => {
                  const street =
                    `${obj.address.streetName || ''} ${obj.address.houseNumber || ''}`.trim();
                  const location =
                    `${obj.address.postalCode || ''} ${obj.address.city || ''}`.trim();
                  return location ? `${street}, ${location}` : street;
                })()
              : '',
            titleForSorting: (obj.title || '').toLowerCase(),
            descriptionForSorting: (obj.description || '').toLowerCase(),
            specificPriceForSorting: this.normalizeForSorting(
              obj.specificPrice || '',
              this.translateService.instant(
                'rem.properties.label__price_on_request'
              )
            ),
            areaSpecificationForSorting: this.normalizeForSorting(
              obj.areaSpecification || ''
            ),
            buildingTypeForSorting:
              (obj.buildingTypes?.length ?? 0) > 0
                ? obj.buildingTypes
                    .map(bt => bt.name.toLowerCase())
                    .sort()
                    .join('|')
                : '',
            isPublishedForSorting: obj.isPublished ? 'Ja' : 'Nein',
            linksForSorting: obj.links?.length
              ? obj.links
                  .map(link =>
                    (link.linkDisplayName || link.link || '').toLowerCase()
                  )
                  .join('|')
              : '',
          };
        });

        this.rentalListingObjects = processedObjects.toSorted((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA;
        });
        this.filteredRentalListingObjects = [...this.rentalListingObjects];
        this.fieldService.getDefaultList();
      },
      error: (error: Error) => {
        this.handleError(error);
        this.isLoadingObjects = false;
      },
      complete: () => (this.isLoadingObjects = false),
    });
  }

  /**
   * Normalisiert einen String für die Sortierung (case-insensitive, ohne Interpunktion).
   * Extrahiert numerische Werte für korrekte numerische Sortierung.
   * @param text Der zu normalisierende Text.
   * @returns Der normalisierte Text für die Sortierung.
   */
  private normalizeForSorting(text: string, defaultText?: string): string {
    if (!text && !defaultText) {
      return '0000000000000|';
    }

    if (!text && defaultText) {
      return '9999999999999|' + defaultText.toLowerCase();
    }
    // Extrahiere numerische Werte
    const numericMatch = text.match(/[\d.,]+/);
    let numericPart = '9999999999999';
    let textPart = text.toLowerCase().trim();

    if (numericMatch) {
      // Entferne Tausendertrennerpunkte und ersetze Komma durch Punkt für Dezimalzahlen
      const numericValue = numericMatch[0].replace(/\./g, '').replace(',', '.');

      const parsedNumber = parseFloat(numericValue);
      if (!isNaN(parsedNumber)) {
        // Padding mit Nullen für einheitliche String-Länge (bis zu 10 Stellen vor dem Komma)
        numericPart = parsedNumber.toFixed(2).padStart(13, '0');

        textPart = text.replace(numericMatch[0], '').toLowerCase().trim();
      }
    }

    return `${numericPart}|${textPart}`;
  }

  /**
   * Handles the selection of a rental listing by ID.
   * @param id The ID of the rental listing.
   */
  onSelectRentalListing(id: number): void {
    this.isLoading = true;

    this.rentalListingClient.getRentalListingObjectForEdit(id).subscribe({
      next: (rentalListingObject: RentalListingDto) => {
        this.openRentalListingEditDialog('edit', rentalListingObject);
        this.isLoading = false;
      },
      error: (error: Error) => {
        this.handleError(error);
        this.isLoading = false;
      },
    });
  }

  /**
   * Opens the rental listing edit dialog.
   * @param rentalListingObject The rental listing object to edit.
   */
  openRentalListingEditDialog(
    mode: 'add' | 'edit',
    rentalListingObject?: RentalListingDto
  ): void {
    this.rentalListingClient
      .getBuildingTypes()
      .pipe(
        finalize(() => {
          const dialogRef = this.dialog.open(RentalListingEditComponent, {
            panelClass: 'smartportal-dialog',
            maxHeight: '800px',
            disableClose: true,
            data: {
              title:
                mode === 'add'
                  ? this.translateService.instant('website_edit.label__create')
                  : this.translateService.instant('website_edit.label__edit'),
              rentalListingObject,
              buildingTypes: this.buildingTypes ?? null,
            },
          });

          dialogRef.afterClosed().subscribe(result => {
            if (result?.cancelled) {
              console.log('Dialog wurde abgebrochen');
            } else {
              this.getRentalListingObjects();
            }
          });
        })
      )
      .subscribe({
        next: types => {
          this.buildingTypes = types;
        },
      });
  }

  /**
   * Shortens the given text to the specified maximum length.
   * @param text The text to shorten.
   * @param maxLength The maximum length of the shortened text.
   * @returns The shortened text with ellipsis if it exceeds the maximum length.
   */
  private static shortenText(text: string, maxLength: number): string {
    if (!text || text.length <= maxLength) {
      return text;
    }

    let shortened = text.substring(0, maxLength);

    const lastSpaceIndex = shortened.lastIndexOf(' ');
    if (lastSpaceIndex > 0) {
      shortened = shortened.substring(0, lastSpaceIndex);
    }

    return shortened + '...';
  }
}
